import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "../../../utils/AuthContext";
import { syncSsoCookie } from "../../../utils/sso";
import {
    User,
    Phone,
    Mail,
    Lock,
    Eye,
    EyeOff,
    ArrowRight,
    Globe,
    Map,
    MapPin,
} from "lucide-react";
import Navbar from "../../navbar/Navbar";
import Footer from "../../footer/Footer";
import {
    BASE_URL,
    studentsApi,
    loginApi,
    countriesApi,
    statesApi,
} from "../../../api/api";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";

const Register = () => {
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [countries, setCountries] = useState([]);
    const [states, setStates] = useState([]);
    const [cities, setCities] = useState([]);
    const [formData, setFormData] = useState({
        profile_picture: null,
        name: "",
        mobile: "",
        email: "",
        password: "",
        confirm_password: "",
        created_at: "",
        status: "1",
        role: "1",
        country: "",
        state: "",
        city: "",
    });

    const navigate = useNavigate();
    const location = useLocation();
    const { token, login } = useAuth();

    // Determine redirect path (pass it to login link if needed)
    const searchParams = new URLSearchParams(location.search);
    const redirectPath = searchParams.get("redirect") || "/dashboard";

    // Auto-set creation date
    useEffect(() => {
        const now = new Date();
        const isoFormat = now.toISOString();
        setFormData((prev) => ({ ...prev, created_at: isoFormat }));
    }, []);

    // Fetch countries on load
    useEffect(() => {
        axios
            .get(`${BASE_URL}${countriesApi}`)
            .then((res) => {
                setCountries(res.data);
                const india = res.data.find(
                    (c) => c.name.toLowerCase() === "india",
                );
                if (india) {
                    setFormData((prev) => ({ ...prev, country: india.id }));
                }
            })
            .catch(() => toast.error("Failed to fetch countries"));
    }, []);

    // Fetch states when country changes
    useEffect(() => {
        if (formData.country) {
            axios
                .get(`${BASE_URL}${statesApi}`, {
                    params: { country: formData.country },
                })
                .then((res) => setStates(res.data))
                .catch(() => toast.error("Failed to fetch states"));
        } else {
            setStates([]);
            setFormData((prev) => ({ ...prev, state: "", city: "" }));
        }
    }, [formData.country]);

    // Fetch cities when state changes
    useEffect(() => {
        if (formData.state) {
            axios
                .get(`${BASE_URL}/cities/bystate/${formData.state}`)
                .then((res) => setCities(res.data))
                .catch(() => toast.error("Failed to fetch cities"));
        } else {
            setCities([]);
            setFormData((prev) => ({ ...prev, city: "" }));
        }
    }, [formData.state]);

    // Handle standard input changes (dropdowns)
    const handleChange = (e) => {
        const { name, value, files } = e.target;
        if (name === "profile_picture") {
            setFormData({ ...formData, [name]: files[0] });
        } else {
            setFormData({ ...formData, [name]: value });
        }
    };

    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();

        const mobile = formData.mobile.replace(/\s/g, "");
        if (!/^[6-9]\d{9}$/.test(mobile)) {
            toast.error("Enter a valid 10-digit Indian mobile number (starting with 6-9)");
            return;
        }

        if (formData.password !== formData.confirm_password) {
            toast.error("Passwords do not match");
            return;
        }

        setIsSubmitting(true);

        try {
            const data = new FormData();
            data.append("name", formData.name);
            data.append("email", formData.email);
            data.append("mobile", formData.mobile);
            data.append("role", formData.role);
            data.append("status", formData.status);
            data.append("created_at", formData.created_at);
            data.append("password", formData.password);
            data.append("password_confirmation", formData.confirm_password);
            data.append("country", formData.country);
            data.append("state", formData.state);
            data.append("city", formData.city);
            if (formData.profile_picture) {
                data.append("profile_picture", formData.profile_picture);
            }

            await axios.post(`${BASE_URL}${studentsApi}`, data);
            toast.success("Account created successfully!");

            // Auto-login: registration returns no token, so call login API
            // with the same credentials to start a session immediately.
            try {
                const loginRes = await axios.post(
                    `${BASE_URL}${loginApi}`,
                    { email: formData.email, password: formData.password },
                    {
                        headers: {
                            Accept: "application/json",
                            "Content-Type": "application/json",
                        },
                    },
                );

                const loginData = loginRes.data;
                if (loginData.success && loginData.token) {
                    const sessionUser = { ...loginData.data, token: loginData.token };
                    login(sessionUser);

                    // Sync SSO cookie (test portal) so later navigation auto-logs in
                    if (loginData.sso_url) {
                        syncSsoCookie(loginData.sso_url);
                    } else if (loginData.token) {
                        syncSsoCookie(loginData.token);
                    }

                    // External redirect (test portal) does full nav so SSO applies
                    if (/^https?:\/\//.test(redirectPath)) {
                        window.location.href = redirectPath;
                    } else {
                        navigate(redirectPath);
                    }
                } else {
                    // Auto-login failed - fall back to login page
                    navigate(`/login?redirect=${encodeURIComponent(redirectPath)}`);
                }
            } catch (loginErr) {
                // Auto-login failed - fall back to login page
                navigate(`/login?redirect=${encodeURIComponent(redirectPath)}`);
            }
        } catch (error) {
            if (error.response?.data?.errors) {
                Object.values(error.response.data.errors).forEach((msg) =>
                    toast.error(msg[0]),
                );
            } else {
                toast.error("Error creating student account");
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    // Animation variants
    const containerVariant = {
        hidden: { opacity: 0, y: 20 },
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                duration: 0.6,
                ease: "easeOut",
                staggerChildren: 0.1,
            },
        },
    };

    const childVariant = {
        hidden: { opacity: 0, x: -20 },
        visible: {
            opacity: 1,
            x: 0,
            transition: { duration: 0.5, ease: "easeOut" },
        },
    };

    // Already logged in? Send to dashboard instead of showing register.
    if (token) {
        return <Navigate to="/dashboard" replace />;
    }

    return (
        <div className="bg-gray-50 flex flex-col relative overflow-hidden">
            {/* Background Pattern */}
            <div className="absolute inset-0 opacity-[0.03] pointer-events-none bg-[linear-gradient(rgba(0,0,0,0.1)_1px,transparent_1px),linear-gradient(90deg,rgba(0,0,0,0.1)_1px,transparent_1px)] [background-size:40px_40px]"></div>

            <main className="flex-grow flex items-center justify-center relative z-10 w-full">
                <motion.div
                    initial="hidden"
                    animate="visible"
                    variants={containerVariant}
                    className="w-full mx-auto flex flex-col lg:flex-row lg:justify-center min-h-[80vh]"
                >
                    {/* LEFT PANEL: Branding & Welcome (Top on Mobile, Left on Desktop) */}
                    <motion.div
                        variants={childVariant}
                        className="w-full aspect-video md:aspect-auto"
                        style={{
                            backgroundImage:
                                "url('/assets/login-illustration.png')",
                            backgroundPosition: "start center",
                            backgroundSize: "cover",
                            backgroundRepeat: "no-repeat",
                        }}
                    ></motion.div>

                    {/* RIGHT PANEL: Registration Form (Bottom on Mobile, Right on Desktop) */}
                    <motion.div
                        variants={childVariant}
                        className="w-full p-12 flex flex-col justify-center"
                    >
                        <div className="max-w-xl w-full mx-auto">
                            <div className="mb-8">
                                <h2 className="text-3xl lg:text-4xl font-bold text-black mb-4 leading-tight">
                                    Create Your{" "}
                                    <span className="text-yellow-500">
                                        Account!
                                    </span>
                                </h2>
                                <p className="text-gray-500 text-sm font-medium">
                                    Join thousands of aspirants who trust us for
                                    their exam preparation journey.
                                </p>
                            </div>

                            <form onSubmit={handleSubmit} className="space-y-5">
                                {/* Row 1: Name & Mobile */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                    <div>
                                        <label className="block text-sm font-bold text-neutral-900 mb-2">
                                            Full Name{" "}
                                            <span className="text-red-500">
                                                *
                                            </span>
                                        </label>
                                        <div className="relative">
                                            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gray-400">
                                                <User size={18} />
                                            </div>
                                            <input
                                                type="text"
                                                name="name"
                                                value={formData.name}
                                                onChange={(e) => {
                                                    let value = e.target.value;
                                                    value = value.replace(
                                                        /[^A-Za-z\s]/g,
                                                        "",
                                                    );
                                                    value = value.replace(
                                                        /\s{2,}/g,
                                                        " ",
                                                    );
                                                    value = value.replace(
                                                        /^\s/,
                                                        "",
                                                    );
                                                    setFormData({
                                                        ...formData,
                                                        name: value,
                                                    });
                                                }}
                                                placeholder="Your Full Name"
                                                required
                                                className="w-full pl-11 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm font-medium text-neutral-900 focus:outline-none focus:bg-white focus:border-yellow-400 focus:ring-1 focus:ring-yellow-400 transition-all placeholder:text-gray-400"
                                            />
                                        </div>
                                    </div>

<div>
                                         <label className="block text-sm font-bold text-neutral-900 mb-2">
                                             Mobile Number{" "}
                                             <span className="text-red-500">
                                                 *
                                             </span>
                                         </label>
                                         <div className="relative">
                                             <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gray-400">
                                                 <Phone size={18} />
                                             </div>
                                             <input
                                                 type="tel"
                                                 name="mobile"
                                                 value={formData.mobile}
                                                 onChange={(e) => {
                                                     let value = e.target.value
                                                         .replace(/[\s\-\(\)]/g, "")
                                                         .replace(/\D/g, "");
                                                     if (value.startsWith("0")) value = value.slice(1);
                                                     if (value.startsWith("91") && value.length > 10) value = value.slice(2);
                                                     if (value.startsWith("91") && value.length === 10) value = value.slice(2);
                                                     if (value.length === 1 && !/[6-9]/.test(value[0])) value = "";
                                                     if (value.length > 10) value = value.slice(0, 10);
                                                     setFormData({ ...formData, mobile: value });
                                                 }}
                                                 onBlur={(e) => {
                                                     const value = e.target.value;
                                                     if (value.length === 10 && /^[6-9]\d{9}$/.test(value)) {
                                                         e.target.value = value.replace(/(\d{5})(\d{5})/, "$1 $2");
                                                     }
                                                 }}
                                                 onFocus={(e) => {
                                                     e.target.value = e.target.value.replace(/\s/g, "");
                                                 }}
                                                 placeholder="10-digit Mobile Number"
                                                 pattern="[6-9][0-9]{9}"
                                                 required
                                                 className="w-full pl-11 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm font-medium text-neutral-900 focus:outline-none focus:bg-white focus:border-yellow-400 focus:ring-1 focus:ring-yellow-400 transition-all placeholder:text-gray-400"
                                             />
                                         </div>
                                     </div>
                                </div>

                                {/* Row 2: Email */}
                                <div>
                                    <label className="block text-sm font-bold text-neutral-900 mb-2">
                                        Email Address{" "}
                                        <span className="text-red-500">*</span>
                                    </label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gray-400">
                                            <Mail size={18} />
                                        </div>
                                        <input
                                            type="email"
                                            name="email"
                                            value={formData.email}
                                            onKeyDown={(e) => {
                                                if (e.key === " ")
                                                    e.preventDefault();
                                            }}
                                            onChange={(e) => {
                                                let value =
                                                    e.target.value.replace(
                                                        /\s/g,
                                                        "",
                                                    );
                                                setFormData({
                                                    ...formData,
                                                    email: value,
                                                });
                                            }}
                                            placeholder="your.email@example.com"
                                            required
                                            className="w-full pl-11 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm font-medium text-neutral-900 focus:outline-none focus:bg-white focus:border-yellow-400 focus:ring-1 focus:ring-yellow-400 transition-all placeholder:text-gray-400"
                                        />
                                    </div>
                                </div>

                                {/* Row 3: Location Dropdowns */}
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                                    <div>
                                        <label className="block text-sm font-bold text-neutral-900 mb-2">
                                            Country{" "}
                                            <span className="text-red-500">
                                                *
                                            </span>
                                        </label>
                                        <div className="relative">
                                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
                                                <Globe size={16} />
                                            </div>
                                            <select
                                                name="country"
                                                value={formData.country}
                                                onChange={handleChange}
                                                required
                                                disabled={countries.length <= 1}
                                                className="w-full pl-10 pr-8 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm font-medium text-neutral-900 appearance-none focus:outline-none focus:bg-white focus:border-yellow-400 focus:ring-1 focus:ring-yellow-400 transition-all cursor-pointer disabled:opacity-70 disabled:cursor-not-allowed"
                                            >
                                                <option value="">Select</option>
                                                {countries.map((c, idx) => (
                                                    <option
                                                        key={idx}
                                                        value={c.id}
                                                    >
                                                        {c.name}
                                                    </option>
                                                ))}
                                            </select>
                                            <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                                                <ChevronDownIcon />
                                            </div>
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-bold text-neutral-900 mb-2">
                                            State{" "}
                                            <span className="text-red-500">
                                                *
                                            </span>
                                        </label>
                                        <div className="relative">
                                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
                                                <Map size={16} />
                                            </div>
                                            <select
                                                name="state"
                                                value={formData.state}
                                                onChange={handleChange}
                                                required
                                                className="w-full pl-10 pr-8 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm font-medium text-neutral-900 appearance-none focus:outline-none focus:bg-white focus:border-yellow-400 focus:ring-1 focus:ring-yellow-400 transition-all cursor-pointer"
                                            >
                                                <option value="">Select</option>
                                                {states.map((s, idx) => (
                                                    <option
                                                        key={idx}
                                                        value={s.id}
                                                    >
                                                        {s.name}
                                                    </option>
                                                ))}
                                            </select>
                                            <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                                                <ChevronDownIcon />
                                            </div>
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-bold text-neutral-900 mb-2">
                                            City{" "}
                                            <span className="text-red-500">
                                                *
                                            </span>
                                        </label>
                                        <div className="relative">
                                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
                                                <MapPin size={16} />
                                            </div>
                                            <select
                                                name="city"
                                                value={formData.city}
                                                onChange={handleChange}
                                                required
                                                className="w-full pl-10 pr-8 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm font-medium text-neutral-900 appearance-none focus:outline-none focus:bg-white focus:border-yellow-400 focus:ring-1 focus:ring-yellow-400 transition-all cursor-pointer"
                                            >
                                                <option value="">Select</option>
                                                {cities.map((c, idx) => (
                                                    <option
                                                        key={idx}
                                                        value={c.id}
                                                    >
                                                        {c.name}
                                                    </option>
                                                ))}
                                            </select>
                                            <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                                                <ChevronDownIcon />
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Row 4: Passwords */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                    <div>
                                        <label className="block text-sm font-bold text-neutral-900 mb-2">
                                            Password{" "}
                                            <span className="text-red-500">
                                                *
                                            </span>
                                        </label>
                                        <div className="relative">
                                            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gray-400">
                                                <Lock size={18} />
                                            </div>
                                            <input
                                                type={
                                                    showPassword
                                                        ? "text"
                                                        : "password"
                                                }
                                                name="password"
                                                value={formData.password}
                                                onChange={(e) => {
                                                    let value =
                                                        e.target.value.replace(
                                                            /\s/g,
                                                            "",
                                                        );
                                                    setFormData({
                                                        ...formData,
                                                        password: value,
                                                    });
                                                }}
                                                placeholder="Create password"
                                                required
                                                className="w-full pl-11 pr-12 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm font-medium text-neutral-900 focus:outline-none focus:bg-white focus:border-yellow-400 focus:ring-1 focus:ring-yellow-400 transition-all placeholder:text-gray-400"
                                            />
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    setShowPassword(
                                                        !showPassword,
                                                    )
                                                }
                                                className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-neutral-900 transition-colors focus:outline-none"
                                            >
                                                {showPassword ? (
                                                    <EyeOff size={18} />
                                                ) : (
                                                    <Eye size={18} />
                                                )}
                                            </button>
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-bold text-neutral-900 mb-2">
                                            Confirm Password{" "}
                                            <span className="text-red-500">
                                                *
                                            </span>
                                        </label>
                                        <div className="relative">
                                            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gray-400">
                                                <Lock size={18} />
                                            </div>
                                            <input
                                                type={
                                                    showConfirm
                                                        ? "text"
                                                        : "password"
                                                }
                                                name="confirm_password"
                                                value={
                                                    formData.confirm_password
                                                }
                                                onChange={(e) => {
                                                    let value =
                                                        e.target.value.replace(
                                                            /\s/g,
                                                            "",
                                                        );
                                                    setFormData({
                                                        ...formData,
                                                        confirm_password: value,
                                                    });
                                                }}
                                                placeholder="Confirm password"
                                                required
                                                className="w-full pl-11 pr-12 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm font-medium text-neutral-900 focus:outline-none focus:bg-white focus:border-yellow-400 focus:ring-1 focus:ring-yellow-400 transition-all placeholder:text-gray-400"
                                            />
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    setShowConfirm(!showConfirm)
                                                }
                                                className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-neutral-900 transition-colors focus:outline-none"
                                            >
                                                {showConfirm ? (
                                                    <EyeOff size={18} />
                                                ) : (
                                                    <Eye size={18} />
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                {/* Terms Checkbox */}
                                <div className="flex items-start gap-3 pt-2">
                                    <div className="flex items-center h-5">
                                        <input
                                            id="accept_terms"
                                            type="checkbox"
                                            required
                                            className="w-4 h-4 border border-gray-300 rounded bg-gray-50 focus:ring-3 focus:ring-yellow-300 accent-neutral-900 cursor-pointer"
                                        />
                                    </div>
                                    <label
                                        htmlFor="accept_terms"
                                        className="text-sm font-medium text-gray-600 cursor-pointer"
                                    >
                                        I agree to the{" "}
                                        <Link
                                            to="/terms-condition"
                                            className="font-bold text-neutral-900 hover:text-red-700 underline decoration-2 underline-offset-2"
                                        >
                                            terms
                                        </Link>{" "}
                                        and{" "}
                                        <Link
                                            to="/privacy-policy"
                                            className="font-bold text-neutral-900 hover:text-red-700 underline decoration-2 underline-offset-2"
                                        >
                                            policy
                                        </Link>
                                        .
                                    </label>
                                </div>

                                {/* Submit Button */}
                                <div className="pt-2">
                                    <button
                                        type="submit"
                                        disabled={isSubmitting}
                                        className="w-full flex items-center cursor-pointer justify-center px-8 py-3.5 bg-neutral-900 hover:bg-yellow-400 hover:text-neutral-900 text-white font-bold rounded-xl shadow-sm transition-all duration-300 group disabled:opacity-70 disabled:cursor-not-allowed"
                                    >
                                        {isSubmitting ? (
                                            <span className="flex items-center">
                                                <svg
                                                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-white group-hover:text-neutral-900"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    fill="none"
                                                    viewBox="0 0 24 24"
                                                >
                                                    <circle
                                                        className="opacity-25"
                                                        cx="12"
                                                        cy="12"
                                                        r="10"
                                                        stroke="currentColor"
                                                        strokeWidth="4"
                                                    ></circle>
                                                    <path
                                                        className="opacity-75"
                                                        fill="currentColor"
                                                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                                    ></path>
                                                </svg>
                                                Creating Account...
                                            </span>
                                        ) : (
                                            <>
                                                Register Now
                                                <ArrowRight
                                                    size={18}
                                                    className="ml-2 transform group-hover:translate-x-1 transition-transform"
                                                />
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>

                            {/* Login Footer */}
                            <div className="mt-8 pt-6 border-t border-gray-100 text-center">
                                <p className="text-sm font-medium text-gray-500">
                                    Already have an account?{" "}
                                    <Link
                                        to={`/login?redirect=${encodeURIComponent(redirectPath)}`}
                                        className="font-bold text-neutral-900 hover:text-red-700 transition-colors underline decoration-2 underline-offset-4"
                                    >
                                        Login here
                                    </Link>
                                </p>
                            </div>
                        </div>
                    </motion.div>
                </motion.div>
            </main>
        </div>
    );
};

// Helper chevron icon for select boxes
const ChevronDownIcon = () => (
    <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="text-gray-400"
    >
        <polyline points="6 9 12 15 18 9"></polyline>
    </svg>
);

export default Register;
